<template lang="pug">
nav.navbar.navbar-light.navbar-expand.flex-column.flex-sm-row.px-3
  .me-1.d-flex
    a.navbar-brand.me-1(:href="link")
      .position-relative.d-inline-block
        img.d-inline-block.align-top.me-1.rounded-circle(
          :src="photo",
          width="30",
          height="30",
          :alt="name"
        )
        img.position-absolute.bottom-0.end-0(
          src="https://pipe.bot/img/logo.svg",
          width="12",
          height="12"
        )
      | {{ name }}
    .py-2(v-if="name") / Maps

  .ms-sm-auto.p-3.p-sm-0
    span.badge.bg-success(v-if="isProBot") PRO
    a.btn.btn-sm.ms-2(v-for="item in submenu", :href="item.link", :target="item.blank ? '_blank' : '_self'")
      font-awesome-icon.fa-fw(v-if="item.icon", :icon="['fas', item.icon]")
      span(v-else) {{ item.name }}

.p-2

#mindmaps.container-fluid(v-cloak)
  loader(v-if="loading")
  .row(v-else)
    .col-12.col-sm-6.col-lg-4.col-xl-3.mb-3
      a.text-decoration-none.d-flex.rounded.border.text-secondary.p-3.h-100.zoom(:href="`/mindmap/${botId}`")
        .mx-auto.my-auto.text-center
          font-awesome-icon.fa-2x.m-2(:icon="['far','plus-circle']")
          p Create new map
    .col-12.col-sm-6.col-lg-4.col-xl-3.mb-3(v-for="map in maps")
      .zoom.shadow-sm.rounded.h-100
        .bg-white.rounded-top.position-relative
          a.stretched-link.d-flex.p-2(style="height: 12rem", :href="`/mindmap/${botId}/${map.id}`")
            img.object-fit-contain(v-if="map.attributes.preview", :alt="map.attributes.title", :src="map.attributes.preview")
            .mx-auto.my-auto(v-else)
              font-awesome-icon.text-black-50.fa-2x(:icon="['far','map']")
          .small.text-muted(v-if="daemons[map.id] && daemons[map.id].attributes.timer")
            .position-absolute.bottom-0.start-0.p-2.px-3
              font-awesome-icon.me-1(:icon="['far','repeat']")
              | {{daemons[map.id].attributes.timer}} sec
            .position-absolute.bottom-0.end-0.p-2 {{`Next launch at ${getDaemonLaunchTime(daemons[map.id])}`}}
        .d-flex
          a.link-dark.text-truncate.text-decoration-none.d-block.p-2.px-3(:href="`/mindmap/${botId}/${map.id}`") {{map.attributes.title}}
          .ms-auto.p-2(v-if="daemons[map.id]")
            .form-check.form-switch
              input.form-check-input.blink(
                :disabled="daemons[map.id].attributes.status === 'working'",
                type="checkbox",
                role="switch",
                :checked="daemons[map.id].attributes.status === 'ready' || daemons[map.id].attributes.status === 'working'",
                @change="onDaemonStatusChange($event, daemons[map.id])"
              )
  v-terminal(v-if="areMessagesReady", :last-messages="lastMessages")

</template>

<script>
import { getBot } from '@/services/api/bot.api'
import { getMaps } from '@/services/api/maps.api'
import { startDaemon, stopDaemon } from '@/services/api/daemon.api'
import { setItem, getItem } from '@/services/storage/local'
import Loader from '@/components/base/Loader'
import Terminal from '@/components/Terminal'

export default {
  components: { Loader, 'v-terminal': Terminal },
  data() {
    return {
      loading: false,
      isContentLoaded: false,
      lastMessages: [{ datetime: Date().substr(16, 5), text: 'Hey 🤘' }],
      defaultFirstMessage: 'Knock, knock, Neo…',
      areMessagesReady: false,
      name: '',
      link: '',
      connection: null,
      photo: 'https://pipe.bot/img/dp.svg',
      botId: '',
      platform: '',
      username: '',
      isProBot: false,
      maps: [],
      submenu: [
        { name: 'Dashboard', link: '', icon: 'analytics' },
        { name: 'Settings', link: '', icon: 'cog' }
      ],
      daemons: []
    }
  },
  async created() {
    await this.downloadContent()
    await this.connectTerminal(this.onSocketMessage)
    this.getLastMessages()
    this.areMessagesReady = true
    if (this.lastMessages.length === 1) {
      await this.$nextTick()
      this.addDefaultMessage()
    }
  },
  methods: {
    addMessage(message) {
      this.lastMessages.push({ datetime: Date().substr(16, 5), text: message })
      this.saveLastMessages()
    },
    changeDaemonStatus({ daemonId, status }) {
      const daemonById = Object.values(this.daemons).find(
        (daemon) => daemon.id === daemonId
      )
      const daemonMapId = daemonById.attributes && daemonById.attributes.mid
      this.daemons[daemonMapId].attributes.status = status
    },
    addDefaultMessage() {
      this.addMessage(this.defaultFirstMessage)
    },
    async downloadContent() {
      let defaultLoadingTimePassed = false
      this.loading = true
      setTimeout(() => {
        defaultLoadingTimePassed = true
        if (this.isContentLoaded) {
          this.loading = false
        }
      }, 750)
      await this.prefetchBot()
      await this.prefetchMaps()
      if (defaultLoadingTimePassed) {
        this.loading = false
      }
      this.isContentLoaded = true
    },
    connectTerminal(callback) {
      const botId = this.$route.params.id
      const clientToken = this.$cookie.get('clientToken')

      console.log(
        `Connection string: ${process.env.VUE_APP_WSS_TERMINAL_HOST}/bot/${botId}/maps?clientToken=${clientToken}`
      )
      this.connection = new WebSocket(
        `${process.env.VUE_APP_WSS_TERMINAL_HOST}/bot/${botId}/maps?clientToken=${clientToken}`
      )
      this.connection.onmessage = function (event) {
        if (event.data) {
          let data = JSON.parse(event.data)
          callback(data)
        }
      }
      let self = this
      window.onbeforeunload = function () {
        self.connection.close()
      }
    },
    onSocketMessage(data) {
      switch (data.action) {
        case 'terminal':
          this.addMessage(data.message)
          break
        case 'daemon':
          this.changeDaemonStatus(data)
          break
      }
    },
    async stopDaemon(daemon) {
      daemon.attributes.status = 'stopped'
      await stopDaemon({ id: daemon.id, apikey: this.apikey })
    },
    async startDaemon(daemon) {
      daemon.attributes.status = 'ready'
      await startDaemon({ id: daemon.id, apikey: this.apikey })
    },
    getDaemonLaunchTime(daemon) {
      const launchTime = new Date(
        Date.parse(daemon.attributes.updated) + daemon.attributes.timer * 1000
      )
      return new Intl.DateTimeFormat('default', {
        hour12: true,
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
      })
        .format(launchTime)
        .toUpperCase()
    },
    onDaemonStatusChange(event, daemon) {
      const isChecked = event.target.checked
      if (isChecked) this.startDaemon(daemon)
      else this.stopDaemon(daemon)
    },
    async prefetchMaps() {
      try {
        const { data, included } = await getMaps({ apikey: this.apikey })
        this.maps = data
        for (const daemon of included) {
          this.daemons[daemon.attributes.mid] = daemon
        }
      } catch (err) {
        console.log('Map error: ' + err)
        window.location.replace('/')
      }
    },
    async prefetchBot() {
      const botId = this.$route.params.id
      const clientToken = this.$cookie.get('clientToken')
      try {
        const { data } = await getBot({ botId, clientToken })
        this.name = data.name
        this.photo = data.photo
        this.botId = data.id
        this.apikey = data.apikey
        this.link = `/bot/${botId}/maps`
        this.username = data.username
        this.platform = data.platform
        this.isProBot = data.product_id
        this.submenu[0].link = `/bot/${botId}/dashboard`
        this.submenu[1].link = `/bot/${botId}/settings`
      } catch (err) {
        console.log('Bot error: ' + err)
        window.location.replace('/')
      }
    },
    getLastMessages() {
      const messages = getItem('lastMessages')
      if (messages && Array.isArray(messages)) {
        this.lastMessages = messages
      }
    },
    saveLastMessages() {
      const messagesCount = 5
      const from = Math.max(this.lastMessages.length - messagesCount, 0)
      const messages = this.lastMessages.slice(from)
      setItem('lastMessages', messages)
    }
  }
}
</script>

<style lang="sass" scoped>
@function tint-color($color, $weight)
  @return mix(white, $color, $weight)

@function shade-color($color, $weight)
  @return mix(black, $color, $weight)

[v-cloak]
  display: none

.object-fit-contain
  height: 100%
  width: 100%
  object-fit: contain

$color-green: #198754
$color-light-green: tint-color($color-green, 30%)
$color-dark-green: shade-color($color-green, 20%)

@keyframes color-blink
  to
    background-color: $color-light-green
    border-color: $color-green

.form-switch .form-check-input:focus:not(:checked)
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23a7c8b6'/%3e%3c/svg%3e")

.form-check-input:focus
  box-shadow: 0 0 0 0.25rem rgb(60 153 110 / 50%)
  border-color: #146c43

.form-switch .form-check-input:focus:not(:checked)
  border-color: $color-green

.zoom
  transition: transform 0.125s ease-out
  &:hover
    transform: scale(1.025)

.pipe-navbar .navbar-nav-scroll
  max-width: 100%
  height: 2.4rem
  overflow: hidden

@media (max-width: 991.98px)
  .pipe-navbar .navbar-nav-scroll .navbar-nav
    padding-bottom: 2rem
    overflow-x: auto
    white-space: nowrap
    -webkit-overflow-scrolling: touch

@media (min-width: 768px)
  @supports (position: -webkit-sticky) or (position: sticky)
    .pipe-navbar
      position: -webkit-sticky
      position: sticky
      top: 0
      z-index: 1040

@media (max-width: 576px)
  .navbar-nav-scroll
    overflow-x: auto
    white-space: nowrap
    overflow: hidden
    font-size: .875rem
    -webkit-overflow-scrolling: touch
    -webkit-box-orient: horizontal
    -webkit-box-direction: normal
  .nav-pills
    font-size: .875rem

@media (min-width: 991.98px)
  .card-columns
    column-count: 6
    &.docs
      column-count: 4

.form-check-input:checked
  background-color: $color-green
  border-color: $color-dark-green
  &.blink
    animation: 1s linear infinite alternate color-blink
</style>
